import { t } from 'i18next';
import { upperFirst } from 'lodash';

import { IAuthUser } from 'auth/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IBaseQuote, IQuoteFile } from 'quotes/types';

import { QuoteFileSource } from './enums';
import { IQuoteDocumentFilters } from './types';

export const getQuoteFileSourceLabel = (entityType: QuoteEntityType, quoteType: QuoteType) => ({
  [QuoteFileSource.OUTLOOK_IMPORT]: t('labelQuoteFileSourceOutlookImport'),
  [QuoteFileSource.BOM_IMPORT]: t('labelQuoteFileSourceBomImport'),
  [QuoteFileSource.SPA_SENT]: t('labelSentAsSpa'),
  [QuoteFileSource.DOWNLOAD]: t('labelQuoteFileSourceQuoteDownload', {
    quoteType: upperFirst(quoteType),
    entityType,
  }),
  [QuoteFileSource.UPLOAD]: t('labelQuoteFileSourceQuoteUpload', {
    quoteType: upperFirst(quoteType),
    entityType,
  }),
  [QuoteFileSource.VENDOR_QUOTE_IMPORT]: t('labelQuoteFileSourceVendorQuoteImport'),
});

export const createUploadDownloadPredicate = (
  record: IQuoteFile,
  filters: IQuoteDocumentFilters
): boolean => {
  const selectedSources: QuoteFileSource[] = [];

  if (filters.upload) {
    selectedSources.push(
      QuoteFileSource.UPLOAD,
      QuoteFileSource.BOM_IMPORT,
      QuoteFileSource.VENDOR_QUOTE_IMPORT
    );
  }

  if (filters.download) {
    selectedSources.push(QuoteFileSource.DOWNLOAD);
  }

  if (selectedSources.length > 0) {
    return selectedSources.includes(record.source);
  }

  return true;
};

export const createSourcesPredicate = (
  record: IQuoteFile,
  filters: IQuoteDocumentFilters
): boolean => {
  if (filters.sources && filters.sources.length > 0) {
    return filters.sources.includes(record.source);
  }

  return true;
};

interface IBomImportVisibilityParams {
  quote?: IBaseQuote | null;
  entityType?: QuoteEntityType;
  bomImportCount?: number;
}

// A BOM cannot be imported into an imported VQ, so the section is normally empty there and only
// misleads. Files are never hidden though: a VQ moved to the imported status through the API can
// still hold earlier BOM imports, and this tab is their only removal affordance. The vendor clause
// is load-bearing: the tab also renders for customer quotes, which can carry EDITABLE_BY_CUSTOMER
// on their own (the shared helper's other call sites are vendor-only).
export const isBomImportSectionVisible = ({
  quote,
  entityType,
  bomImportCount = 0,
}: IBomImportVisibilityParams): boolean =>
  bomImportCount > 0 ||
  !(
    QuoteType.VENDOR === quote?.type &&
    VendorQuoteHelper.isImportedVendorQuote({ quote, quoteEntityType: entityType })
  );

// Empty, the section shows only where this user could send (the vendor clause: customer quotes can
// carry the imported status on their own). Sent files show to anyone who can open the quote, and
// stay visible after the quote or the company loses the capability.
export const isSpaSentSectionVisible = ({
  quote,
  user,
  hasSendAsSpaRight,
  spaSentCount = 0,
}: {
  quote?: IBaseQuote | null;
  user?: IAuthUser | null;
  hasSendAsSpaRight: boolean;
  spaSentCount?: number;
}): boolean =>
  spaSentCount > 0 ||
  (hasSendAsSpaRight &&
    QuoteType.VENDOR === quote?.type &&
    VendorQuoteHelper.isSkuExportAvailable({ quote, user }));

// Names are null when the user row is gone.
export const getQuoteFileUserName = (quoteFile: IQuoteFile): string =>
  [quoteFile.userFirstName, quoteFile.userLastName]
    .map((name) => name?.trim())
    .filter(Boolean)
    .join(' ') || DEFAULT_PLACEHOLDER;
